import styles from './scenarioModal.module.scss';
import { useTranslation } from "react-i18next";
import { Input } from "../../shared/components/input/input";
import {
  useCreateScenarioMutation,
  useDeleteScenarioMutation,
  useUpdateScenarioMutation,
} from "../../api/scenarioService";
import {
  useCallback,
  useEffect,
  useState
} from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  number,
  object,
  string
} from "yup";
import { Scenario } from "../../types/scenarioTypes";
import { MessageWindow } from "../../shared/components/messageWindow/messageWindow";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { setUpdateModal } from "../../store/slices/mainSlice";
import { ModalWindow } from "../../shared/components/modalWindow/modalWindow";
import { TextArea } from "../../shared/components/textArea/textArea";

type ScenarioModalProps = {
  onClose: (isSave: boolean, idCreate?: number) => void;
  scenario?: Scenario
}

export const ScenarioModal = ({ onClose, scenario }: ScenarioModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>()

  const [showDeleteMessage, setShowDeleteMessage] = useState<boolean>(false);

  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const [createScenario, { data: createData, isSuccess: isSuccessCreateScenario, isLoading: isLoadingCreate }] = useCreateScenarioMutation();
  const [updateScenario, { isSuccess: isSuccessUpdateScenario, isLoading: isLoadingUpdate }] = useUpdateScenarioMutation();
  const [deleteScenario, { isSuccess: isSuccessDeleteScenario, isLoading: isLoadingDelete }] = useDeleteScenarioMutation();

  useEffect(() => {
    if (isSuccessCreateScenario && createData) {
      dispatch(setUpdateModal({ show: true, isDelete: false }));
      onClose(true, createData);
    }
  }, [isSuccessCreateScenario]);

  useEffect(() => {
    if (isSuccessUpdateScenario) {
      dispatch(setUpdateModal({ show: true, isDelete: false }));
      onClose(true);
    }
  }, [isSuccessUpdateScenario]);

  useEffect(() => {
    if (isSuccessDeleteScenario) {
      dispatch(setUpdateModal({ show: true, isDelete: true }));
      onClose(true);
    }
  }, [isSuccessDeleteScenario]);


  const schema = object({
    name: string().required(t("common.notValid")).typeError(t("common.notValid")),
    maximumExecutionTimeout: number().required(t("common.notValid")).typeError(t("common.notValid"))
      .max(10000, t("common.notValid")).min(1, t("common.notValid")),
    description: string(),
  })

  const { control, handleSubmit, formState: { errors, isDirty } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: scenario?.name || "",
      maximumExecutionTimeout: scenario?.maximumExecutionTimeout || 0,
      description: scenario?.description || ""
    },
    mode: "onChange"
  });

  const onCancel = useCallback(() => {
    if (isDirty) {
      setShowConfirmCancel(true);
    } else {
      onClose(false);
    }
  }, [isDirty, onClose]);

  const closeConfirmCancelModal = useCallback((isConfirm: boolean) => {
    if (isConfirm) {
      onClose(false);
    }
    setShowConfirmCancel(false);
  }, [setShowConfirmCancel]);

  const onClickSave = (): void => {
    handleSubmit((value) => {
      const newScenario: Scenario = {
        description: value.description,
        name: value.name,
        maximumExecutionTimeout: value.maximumExecutionTimeout,
        assignedRequests: []
      };
      if (!!scenario) {
        newScenario.id = scenario.id;
        updateScenario(newScenario);
      } else {
        createScenario(newScenario);
      }
    })();
  }

  const onDeleteClick = (): void => {
    setShowDeleteMessage(true);
  }

  const closeMessageWindow = (isConfirm: boolean): void => {
    if (isConfirm && !!scenario?.id) {
      deleteScenario(scenario.id);
    }
    setShowDeleteMessage(false);
  }

  return (
    <>
      <ModalWindow title={!!scenario ? t("scenario.edit") : t("scenario.add")}
                   onClose={onCancel}
                   width={380}
                   children={
                     <div className={styles.container}>
                       <div className={styles.inputContainer}>
                         <span className={styles.required}>{t("common.name")}</span>
                         <div className={styles.input}>
                           <Input control={control}
                                  name={"name"}
                                  maxLength={100}
                                  error={!!errors.name ? errors.name.message : ""}/>
                         </div>
                       </div>
                       <div className={styles.maxExecutionInputContainer}>
                         <span className={styles.required}>{t("common.maxExecutionTimeout")}</span>
                         <div className={styles.input}>
                           <Input control={control}
                                  name={"maximumExecutionTimeout"}
                                  maxLength={4}
                                  type={"number"}
                                  error={!!errors.maximumExecutionTimeout ? errors.maximumExecutionTimeout.message : ""}/>
                         </div>
                       </div>
                       <div className={styles.descriptionContainer}>
                         <span>{t("common.description")}</span>
                         <TextArea control={control}
                                   name={"description"}
                                   maxLength={57000}/>
                       </div>
                       <div className={styles.buttonsContainer}>
                         <div className={styles.buttons}>
                           {!!scenario && (
                             <button className={styles.delete}
                                     disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                     onClick={onDeleteClick}>{t("common.delete")}</button>
                           )}
                         </div>
                         <div className={styles.buttons}>
                           <button className={styles.cancel}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={onCancel}>{t("common.cancel")}</button>
                           <button className={styles.save}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={onClickSave}>{t("common.save")}</button>
                         </div>
                       </div>
                     </div>
                   }
      />

      {showDeleteMessage && (
        <MessageWindow onClose={closeMessageWindow}
                       title={t("scenario.delete")}
                       description={t("scenario.deleteDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}

      {showConfirmCancel && (
        <MessageWindow onClose={closeConfirmCancelModal}
                       title={t("common.cancelChanges")}
                       description={t("common.cancelChangesDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}
    </>

  );
}